/**
 * Flows API Service
 * Handles all Sankey flow visualization API calls
 * Based on SPECIFICATION.md Section 6 and 7.9
 */
import api from './api';
import type { FlowData, FlowNode, FlowEdge, PeriodType, PaginatedResponse } from '@/types';

// ============================================================================
// Types
// ============================================================================

export type FlowStatus = 'pending' | 'processing' | 'ready' | 'error';

export interface Flow {
  id: string;
  tenantId: string;
  name?: string;
  periodType: PeriodType;
  periodStart: string;
  periodEnd: string;
  accountIds?: string[];
  status: FlowStatus;
  computedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface FlowSummary {
  totalIncome: number;
  totalExpenses: number;
  netFlow: number;
  savingsRate: number;
  topIncomeCategories: CategorySummary[];
  topExpenseCategories: CategorySummary[];
}

export interface CategorySummary {
  categoryId: string;
  categoryName: string;
  amount: number;
  percentage: number;
}

/**
 * Transfer entry from backend API (WP-TRANSFER-2)
 */
export interface TransferEntry {
  id: string;
  sourceAccountId: string | null;
  sourceAccountName: string;
  targetAccountId: string | null;
  targetAccountName: string;
  amount: number;
  subcategory: string;
  transferType: 'paired' | 'single_outgoing' | 'single_incoming';
  transactionIds: string[];
}

/**
 * Transfer section from backend API (WP-TRANSFER-2)
 * @deprecated Use TransferSankeySection instead (WP-FLOW-REFACTOR-2)
 */
export interface TransferSection {
  transfers: TransferEntry[];
  totalCount: number;
  totalAmount: number;
}

// ============================================================================
// Transfer Sankey Types (WP-FLOW-REFACTOR-2)
// ============================================================================

/**
 * Node in the transfer Sankey diagram
 */
export interface TransferSankeyNode {
  id: string;
  type: 'transfer_source' | 'transfer_target' | 'external';
  name: string;
  accountId: string | null;
  totalAmount: number;
  isExternal: boolean;
}

/**
 * Edge connecting source and target nodes in the transfer Sankey
 */
export interface TransferSankeyEdge {
  id: string;
  sourceNodeId: string;
  targetNodeId: string;
  amount: number;
  transactionIds: string[];
  transactionCount: number;
}

/**
 * Transfer Sankey data from backend API (WP-FLOW-REFACTOR-2)
 */
export interface TransferSankeySection {
  sourceNodes: TransferSankeyNode[];
  targetNodes: TransferSankeyNode[];
  edges: TransferSankeyEdge[];
  totalAmount: number;
  totalCount: number;
}

export interface FlowSnapshot {
  flow: Flow;
  periodType: PeriodType;
  periodStart: string;
  periodEnd: string;
  nodes: FlowNode[];
  edges: FlowEdge[];
  summary: FlowSummary;
  /** @deprecated Use transferSankey instead (WP-FLOW-REFACTOR-2) */
  transferSection?: TransferSection;
  /** WP-FLOW-REFACTOR-2: Transfer Sankey data for display below main diagram */
  transferSankey?: TransferSankeySection;
  computedAt: string;
}

export interface FlowNodeDetail {
  node: FlowNode;
  children: FlowNode[];
  childEdges: FlowEdge[];
  parentEdge?: FlowEdge;
}

export interface FlowEdgeDetail {
  edge: FlowEdge;
  sourceName: string;
  targetName: string;
  transactions: FlowEdgeTransaction[];
}

export interface FlowEdgeTransaction {
  id: string;
  date: string;
  name: string;
  merchantName?: string;
  amount: number;
  accountId: string;
  accountName: string;
}

export interface FlowSummaryPeriod {
  periodType: PeriodType;
  periodStart: string;
  periodEnd: string;
  label: string;
  totalIncome: number;
  totalExpenses: number;
  netFlow: number;
  savingsRate: number;
}

export interface MultiPeriodFlowSummary {
  periods: FlowSummaryPeriod[];
  averageIncome: number;
  averageExpenses: number;
  averageNetFlow: number;
  trend: 'improving' | 'stable' | 'declining';
}

export interface FlowParams {
  periodType: PeriodType;
  periodStart: string;
  periodEnd?: string;
  forceRefresh?: boolean;
}

export interface FlowNodeParams {
  periodType: PeriodType;
  periodStart: string;
  nodeId: string;
}

export interface FlowEdgeParams {
  periodType: PeriodType;
  periodStart: string;
  sourceId: string;
  targetId: string;
  page?: number;
  pageSize?: number;
}

export interface FlowSummaryParams {
  periodType: PeriodType;
  count?: number;
}

export interface CreateFlowRequest {
  name?: string;
  periodType: PeriodType;
  periodStart: string;
  periodEnd?: string;
  accountIds?: string[];
}

export interface FlowRefreshResponse {
  flow: Flow;
  status: 'queued' | 'processing' | 'completed';
  estimatedCompletionTime?: string;
}

export interface FlowExportPdfRequest {
  includeAccountBreakdown?: boolean;
  paperSize?: 'auto' | 'a4' | 'letter';
}

export interface FlowExportPdfResponse {
  downloadUrl: string;
  expiresAt: string;
}

// ============================================================================
// Response Transformation Helpers
// ============================================================================

/**
 * Transform a raw API FlowNode response to match frontend FlowNode type.
 * Backend uses `name` field, but spec and frontend expect `label`.
 * See domain-model.md line 216: nodes (JSONB) -- [{id, label, type, total, ...}]
 */
function transformFlowNode(node: Record<string, unknown>): FlowNode {
  // Preserve metadata from backend (contains account_count, aggregate_inflow, etc.)
  const metadata = node.metadata as FlowNode['metadata'] | undefined;

  return {
    id: node.id as string,
    label: (node.name ?? node.label) as string,  // Backend sends `name`, spec says `label`
    type: node.type as 'income' | 'expense' | 'transfer' | 'account' | 'lumped_account',
    total: Number(node.value ?? 0),
    entityId: (node.entityId as string | undefined) ??
              (node.entity_id as string | undefined) ??
              (metadata?.categoryId as string | undefined) ??
              (metadata?.category_id as string | undefined) ??
              (metadata?.accountId as string | undefined) ??
              (metadata?.account_id as string | undefined) ??
              undefined,
    entityType: (node.entityType as 'category' | 'account' | 'lumped' | undefined) ??
                (node.entity_type as 'category' | 'account' | 'lumped' | undefined) ??
                (node.type === 'account' || node.type === 'lumped_account' ? 'account' : 'category'),
    tier: (node.tier as 'A' | 'B' | 'C' | undefined),  // Preserve tier for account nodes
    metadata,  // Preserve metadata for lumped_account nodes (contains account_count)
    layer: (node.layer ?? node.x ?? 0) as number,
    hasChildren: false,
    childCount: 0,
    depth: 0,
    path: '',
  };
}

/**
 * Transform a raw API FlowEdge response to match frontend FlowEdge type.
 */
function transformFlowEdge(edge: Record<string, unknown>): FlowEdge {
  return {
    source: edge.source as string,
    target: edge.target as string,
    value: Number(edge.value ?? 0),
    transactions: (edge.transactionIds as string[] | undefined)?.length ??
                  (edge.transaction_ids as string[] | undefined)?.length ?? 0,
  };
}

/**
 * Transform a raw transfer entry from API response (WP-TRANSFER-2)
 * @deprecated Use transformTransferSankeyNode/Edge instead (WP-FLOW-REFACTOR-2)
 */
function transformTransferEntry(entry: Record<string, unknown>): TransferEntry {
  return {
    id: (entry.id ?? '') as string,
    sourceAccountId: (entry.sourceAccountId ?? entry.source_account_id ?? null) as string | null,
    sourceAccountName: (entry.sourceAccountName ?? entry.source_account_name ?? '[External]') as string,
    targetAccountId: (entry.targetAccountId ?? entry.target_account_id ?? null) as string | null,
    targetAccountName: (entry.targetAccountName ?? entry.target_account_name ?? '[External]') as string,
    amount: Number(entry.amount ?? 0),
    subcategory: (entry.subcategory ?? 'Transfer') as string,
    transferType: (entry.transferType ?? entry.transfer_type ?? 'paired') as 'paired' | 'single_outgoing' | 'single_incoming',
    transactionIds: ((entry.transactionIds ?? entry.transaction_ids ?? []) as string[]),
  };
}

/**
 * Transform a raw transfer Sankey node from API response (WP-FLOW-REFACTOR-2)
 */
function transformTransferSankeyNode(node: Record<string, unknown>): TransferSankeyNode {
  return {
    id: (node.id ?? '') as string,
    type: (node.type ?? 'external') as 'transfer_source' | 'transfer_target' | 'external',
    name: (node.name ?? '[External]') as string,
    accountId: (node.accountId ?? node.account_id ?? null) as string | null,
    totalAmount: Number(node.totalAmount ?? node.total_amount ?? 0),
    isExternal: Boolean(node.isExternal ?? node.is_external ?? false),
  };
}

/**
 * Transform a raw transfer Sankey edge from API response (WP-FLOW-REFACTOR-2)
 */
function transformTransferSankeyEdge(edge: Record<string, unknown>): TransferSankeyEdge {
  const transactionIds = (edge.transactionIds ?? edge.transaction_ids ?? []) as string[];
  return {
    id: (edge.id ?? '') as string,
    sourceNodeId: (edge.sourceNodeId ?? edge.source_node_id ?? '') as string,
    targetNodeId: (edge.targetNodeId ?? edge.target_node_id ?? '') as string,
    amount: Number(edge.amount ?? 0),
    transactionIds,
    transactionCount: Number(edge.transactionCount ?? edge.transaction_count ?? transactionIds.length),
  };
}

/**
 * Transform a raw API response to a FlowSnapshot.
 * Handles both camelCase and snake_case field names from the API.
 */
function transformFlowSnapshotResponse(response: Record<string, unknown>): FlowSnapshot {
  // Transform nodes and edges
  const rawNodes = (response.nodes ?? []) as Record<string, unknown>[];
  const rawEdges = (response.edges ?? []) as Record<string, unknown>[];
  const nodes = rawNodes.map(transformFlowNode);
  const edges = rawEdges.map(transformFlowEdge);

  // Get totals (prefer camelCase)
  const totals = (response.totals ?? {}) as Record<string, unknown>;

  // WP-TRANSFER-2: Transform transfer section (deprecated, for backward compatibility)
  const rawTransferSection = (response.transferSection ?? response.transfer_section) as Record<string, unknown> | undefined;
  let transferSection: TransferSection | undefined;
  if (rawTransferSection) {
    const rawTransfers = (rawTransferSection.transfers ?? []) as Record<string, unknown>[];
    transferSection = {
      transfers: rawTransfers.map(transformTransferEntry),
      totalCount: Number(rawTransferSection.totalCount ?? rawTransferSection.total_count ?? rawTransfers.length),
      totalAmount: Number(rawTransferSection.totalAmount ?? rawTransferSection.total_amount ?? 0),
    };
  }

  // WP-FLOW-REFACTOR-2: Transform transfer Sankey data
  const rawTransferSankey = (response.transferSankey ?? response.transfer_sankey) as Record<string, unknown> | undefined;
  let transferSankey: TransferSankeySection | undefined;
  if (rawTransferSankey) {
    const rawSourceNodes = (rawTransferSankey.sourceNodes ?? rawTransferSankey.source_nodes ?? []) as Record<string, unknown>[];
    const rawTargetNodes = (rawTransferSankey.targetNodes ?? rawTransferSankey.target_nodes ?? []) as Record<string, unknown>[];
    const rawEdges = (rawTransferSankey.edges ?? []) as Record<string, unknown>[];
    transferSankey = {
      sourceNodes: rawSourceNodes.map(transformTransferSankeyNode),
      targetNodes: rawTargetNodes.map(transformTransferSankeyNode),
      edges: rawEdges.map(transformTransferSankeyEdge),
      totalAmount: Number(rawTransferSankey.totalAmount ?? rawTransferSankey.total_amount ?? 0),
      totalCount: Number(rawTransferSankey.totalCount ?? rawTransferSankey.total_count ?? 0),
    };
  }

  return {
    flow: {
      id: response.id as string,
      tenantId: (response.tenantId ?? response.tenant_id ?? '') as string,
      name: response.name as string | undefined,
      periodType: (response.periodType ?? response.period_type) as PeriodType,
      periodStart: (response.periodStart ?? response.period_start) as string,
      periodEnd: (response.periodEnd ?? response.period_end) as string,
      accountIds: (response.accountIds ?? response.account_ids) as string[] | undefined,
      status: ((response.isStale ?? response.is_stale) ? 'pending' : 'ready') as FlowStatus,
      computedAt: (response.computedAt ?? response.computed_at) as string | undefined,
      createdAt: (response.createdAt ?? response.created_at) as string,
      updatedAt: (response.updatedAt ?? response.updated_at) as string,
    },
    periodType: (response.periodType ?? response.period_type) as PeriodType,
    periodStart: (response.periodStart ?? response.period_start) as string,
    periodEnd: (response.periodEnd ?? response.period_end) as string,
    nodes,
    edges,
    summary: {
      totalIncome: Number(totals.totalIncome ?? totals.total_income ?? 0),
      totalExpenses: Number(totals.totalExpense ?? totals.total_expense ?? 0),
      netFlow: Number(totals.netFlow ?? totals.net_flow ?? 0),
      savingsRate: Number(totals.savingsRate ?? totals.savings_rate ?? 0),
      topIncomeCategories: [],
      topExpenseCategories: [],
    },
    transferSection,
    transferSankey,
    computedAt: (response.computedAt ?? response.computed_at) as string,
  };
}

// ============================================================================
// Flows Service
// ============================================================================

export const flowsService = {
  /**
   * Get flow data for a specific period (main Sankey visualization endpoint)
   */
  getFlow: async (params: FlowParams): Promise<FlowSnapshot> => {
    const { periodType, periodStart, periodEnd, forceRefresh } = params;
    const queryParams: Record<string, unknown> = {};

    if (periodEnd) queryParams.periodEnd = periodEnd;
    if (forceRefresh) queryParams.forceRefresh = forceRefresh;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const response = await api.get<any>(`/flows/${periodType}/${periodStart}`, queryParams);
    return transformFlowSnapshotResponse(response);
  },

  /**
   * Get simplified flow data for visualization
   */
  getFlowData: async (params: FlowParams): Promise<FlowData> => {
    const snapshot = await flowsService.getFlow(params);

    return {
      periodType: snapshot.periodType,
      periodStart: snapshot.periodStart,
      periodEnd: snapshot.periodEnd,
      nodes: snapshot.nodes,
      edges: snapshot.edges,
      totalIncome: snapshot.summary.totalIncome,
      totalExpenses: snapshot.summary.totalExpenses,
      netFlow: snapshot.summary.netFlow,
    };
  },

  /**
   * Get node detail with children (for drill-down)
   */
  getNodeDetail: async (params: FlowNodeParams): Promise<FlowNodeDetail> => {
    const { periodType, periodStart, nodeId } = params;
    return api.get<FlowNodeDetail>(`/flows/${periodType}/${periodStart}/node/${nodeId}`);
  },

  /**
   * Get edge detail with transactions
   */
  getEdgeDetail: async (params: FlowEdgeParams): Promise<FlowEdgeDetail> => {
    const { periodType, periodStart, sourceId, targetId, page, pageSize } = params;
    const queryParams: Record<string, unknown> = {};

    if (page !== undefined) queryParams.page = page;
    if (pageSize !== undefined) queryParams.pageSize = pageSize;

    return api.get<FlowEdgeDetail>(
      `/flows/${periodType}/${periodStart}/edge/${sourceId}/${targetId}`,
      queryParams
    );
  },

  /**
   * Get transactions for a specific edge (paginated)
   */
  getEdgeTransactions: async (
    params: FlowEdgeParams
  ): Promise<PaginatedResponse<FlowEdgeTransaction>> => {
    const { periodType, periodStart, sourceId, targetId, page, pageSize } = params;
    const queryParams: Record<string, unknown> = {};

    if (page !== undefined) queryParams.page = page;
    if (pageSize !== undefined) queryParams.pageSize = pageSize;

    return api.get<PaginatedResponse<FlowEdgeTransaction>>(
      `/flows/${periodType}/${periodStart}/edge/${sourceId}/${targetId}/transactions`,
      queryParams
    );
  },

  /**
   * Get flow summary for multiple periods
   */
  getSummary: async (params: FlowSummaryParams): Promise<MultiPeriodFlowSummary> => {
    const queryParams: Record<string, unknown> = {
      periodType: params.periodType,
    };

    if (params.count !== undefined) queryParams.count = params.count;

    return api.get<MultiPeriodFlowSummary>('/flows/summary', queryParams);
  },

  /**
   * Force recomputation of flow data for a period
   */
  refresh: async (params: FlowParams): Promise<FlowRefreshResponse> => {
    const { periodType, periodStart, periodEnd } = params;
    const body: Record<string, unknown> = {
      periodType,
      periodStart,
    };

    if (periodEnd) body.periodEnd = periodEnd;

    return api.post<FlowRefreshResponse>('/flows/refresh', body);
  },

  // ============================================================================
  // Flow CRUD Operations
  // ============================================================================

  /**
   * List all saved flows
   */
  getAll: async (): Promise<Flow[]> => {
    // Backend returns { snapshots, total, limit, offset }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const response = await api.get<any>('/flows');

    // Transform snapshots to Flow format
    const snapshots = response.snapshots ?? response.flows ?? [];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return snapshots.map((s: any) => ({
      id: s.id,
      tenantId: s.tenantId ?? s.tenant_id ?? '',
      name: s.name,
      periodType: s.periodType ?? s.period_type,
      periodStart: s.periodStart ?? s.period_start,
      periodEnd: s.periodEnd ?? s.period_end,
      accountIds: s.accountIds ?? s.account_ids,
      status: (s.isStale ?? s.is_stale) ? 'pending' : 'ready',
      computedAt: s.computedAt ?? s.computed_at,
      createdAt: s.createdAt ?? s.created_at,
      updatedAt: s.updatedAt ?? s.updated_at,
    }));
  },

  /**
   * Get a specific flow by ID
   */
  getById: async (id: string): Promise<Flow> => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const response = await api.get<any>(`/flows/${id}`);

    return {
      id: response.id,
      tenantId: response.tenantId ?? response.tenant_id ?? '',
      name: response.name,
      periodType: response.periodType ?? response.period_type,
      periodStart: response.periodStart ?? response.period_start,
      periodEnd: response.periodEnd ?? response.period_end,
      accountIds: response.accountIds ?? response.account_ids,
      status: (response.isStale ?? response.is_stale) ? 'pending' : 'ready',
      computedAt: response.computedAt ?? response.computed_at,
      createdAt: response.createdAt ?? response.created_at,
      updatedAt: response.updatedAt ?? response.updated_at,
    };
  },

  /**
   * Get the snapshot for a specific flow by ID
   * The backend GET /flows/{id} endpoint returns full snapshot data including nodes/edges
   */
  getSnapshot: async (id: string): Promise<FlowSnapshot> => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const response = await api.get<any>(`/flows/${id}`);
    return transformFlowSnapshotResponse(response);
  },

  /**
   * Create a new custom flow
   * Uses the /flows/generate/save endpoint to generate and save in one request
   */
  create: async (data: CreateFlowRequest): Promise<Flow> => {
    // Helper to format date as YYYY-MM-DD using local date components
    const formatLocalDate = (date: Date): string => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    // Helper to parse ISO string as local date
    const parseLocalDate = (isoString: string): Date => {
      const parts = isoString.split('-').map(Number);
      const year = parts[0] ?? 2000;
      const month = parts[1] ?? 1;
      const day = parts[2] ?? 1;
      return new Date(year, month - 1, day);
    };

    // Calculate periodEnd based on period type if not provided
    let periodEnd = data.periodEnd;
    if (!periodEnd) {
      const startDate = parseLocalDate(data.periodStart);
      switch (data.periodType) {
        case 'month': {
          const endDate = new Date(startDate.getFullYear(), startDate.getMonth() + 1, 0);
          periodEnd = formatLocalDate(endDate);
          break;
        }
        case 'quarter': {
          const endDate = new Date(startDate.getFullYear(), startDate.getMonth() + 3, 0);
          periodEnd = formatLocalDate(endDate);
          break;
        }
        case 'year': {
          periodEnd = `${startDate.getFullYear()}-12-31`;
          break;
        }
        case 'ytd': {
          // Year to date: today
          periodEnd = formatLocalDate(new Date());
          break;
        }
        default:
          // For custom, use end of month as fallback
          periodEnd = formatLocalDate(new Date(startDate.getFullYear(), startDate.getMonth() + 1, 0));
      }
    }

    const requestBody = {
      periodType: data.periodType,
      periodStart: data.periodStart,
      periodEnd: periodEnd,
      excludeTransfers: true,
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const response = await api.post<any>('/flows/generate/save', requestBody);

    return {
      id: response.id,
      tenantId: response.tenantId ?? response.tenant_id ?? '',
      name: data.name, // Use the name from the request since backend doesn't store name
      periodType: response.periodType ?? response.period_type ?? data.periodType,
      periodStart: response.periodStart ?? response.period_start ?? data.periodStart,
      periodEnd: response.periodEnd ?? response.period_end ?? periodEnd,
      accountIds: data.accountIds,
      status: (response.isStale ?? response.is_stale) ? 'pending' : 'ready',
      computedAt: response.computedAt ?? response.computed_at,
      createdAt: response.createdAt ?? response.created_at,
      updatedAt: response.updatedAt ?? response.updated_at,
    };
  },

  /**
   * Update a flow
   */
  update: async (id: string, data: { name?: string }): Promise<Flow> => {
    return api.put<Flow>(`/flows/${id}`, data);
  },

  /**
   * Delete a saved flow
   */
  delete: async (id: string): Promise<void> => {
    return api.delete(`/flows/${id}`);
  },

  /**
   * Export a flow snapshot as a PDF
   * @param id - Flow snapshot UUID
   * @param options - PDF export options
   * @returns Download URL and expiry
   */
  exportPdf: async (id: string, options?: FlowExportPdfRequest): Promise<FlowExportPdfResponse> => {
    const body = {
      includeAccountBreakdown: options?.includeAccountBreakdown ?? true,
      paperSize: options?.paperSize ?? 'auto',
    };
    return api.post<FlowExportPdfResponse>(`/flows/${id}/export-pdf`, body);
  },

  /**
   * Force regeneration of a flow snapshot and return fresh data.
   *
   * This is an atomic operation that regenerates the flow with current
   * transaction data and returns the fresh snapshot in a single request.
   * Use this when the user explicitly requests a refresh.
   *
   * @param id - Flow snapshot UUID to regenerate
   * @returns Freshly regenerated flow snapshot with updated computedAt
   */
  regenerate: async (id: string): Promise<FlowSnapshot> => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const response = await api.post<any>(`/flows/${id}/regenerate`);
    return transformFlowSnapshotResponse(response);
  },

  // ============================================================================
  // Convenience Methods
  // ============================================================================

  /**
   * Get flow for current month
   */
  getCurrentMonth: async (): Promise<FlowSnapshot> => {
    const now = new Date();
    const periodStart = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;

    return flowsService.getFlow({
      periodType: 'month',
      periodStart,
    });
  },

  /**
   * Get flow for last 30 days (rolling window)
   * WP-DASH-2: Used by dashboard mini Sankey visualization
   */
  getLast30Days: async (): Promise<FlowSnapshot> => {
    const now = new Date();
    // Use today as the reference point for the rolling window
    const periodStart = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

    return flowsService.getFlow({
      periodType: 'last_30_days',
      periodStart,
    });
  },

  /**
   * Get flow for previous month
   */
  getPreviousMonth: async (): Promise<FlowSnapshot> => {
    const now = new Date();
    now.setMonth(now.getMonth() - 1);
    const periodStart = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;

    return flowsService.getFlow({
      periodType: 'month',
      periodStart,
    });
  },

  /**
   * Get flow for current quarter
   */
  getCurrentQuarter: async (): Promise<FlowSnapshot> => {
    const now = new Date();
    const quarter = Math.floor(now.getMonth() / 3);
    const periodStart = `${now.getFullYear()}-${String(quarter * 3 + 1).padStart(2, '0')}-01`;

    return flowsService.getFlow({
      periodType: 'quarter',
      periodStart,
    });
  },

  /**
   * Get flow for current year
   */
  getCurrentYear: async (): Promise<FlowSnapshot> => {
    const now = new Date();
    const periodStart = `${now.getFullYear()}-01-01`;

    return flowsService.getFlow({
      periodType: 'year',
      periodStart,
    });
  },

  /**
   * Get monthly summary for the last N months
   */
  getMonthlySummary: async (count: number = 6): Promise<MultiPeriodFlowSummary> => {
    return flowsService.getSummary({
      periodType: 'month',
      count,
    });
  },

  /**
   * Compare two periods
   */
  comparePeriods: async (
    period1: FlowParams,
    period2: FlowParams
  ): Promise<{
    period1: FlowSummary;
    period2: FlowSummary;
    incomeChange: number;
    expenseChange: number;
    netFlowChange: number;
    incomeChangePercent: number;
    expenseChangePercent: number;
    netFlowChangePercent: number;
  }> => {
    const [snapshot1, snapshot2] = await Promise.all([
      flowsService.getFlow(period1),
      flowsService.getFlow(period2),
    ]);

    const incomeChange = snapshot2.summary.totalIncome - snapshot1.summary.totalIncome;
    const expenseChange = snapshot2.summary.totalExpenses - snapshot1.summary.totalExpenses;
    const netFlowChange = snapshot2.summary.netFlow - snapshot1.summary.netFlow;

    return {
      period1: snapshot1.summary,
      period2: snapshot2.summary,
      incomeChange,
      expenseChange,
      netFlowChange,
      incomeChangePercent: snapshot1.summary.totalIncome
        ? (incomeChange / snapshot1.summary.totalIncome) * 100
        : 0,
      expenseChangePercent: snapshot1.summary.totalExpenses
        ? (expenseChange / snapshot1.summary.totalExpenses) * 100
        : 0,
      netFlowChangePercent: snapshot1.summary.netFlow
        ? (netFlowChange / Math.abs(snapshot1.summary.netFlow)) * 100
        : 0,
    };
  },
};

export default flowsService;
